/* eslint-disable no-console */
import { useState, useCallback } from 'react';
import { Contract } from '@ethersproject/contracts';

import { useWeb3React } from './web3';
import { calculateGasMargin, TX_STATUS, logRevert } from 'web3/utils';
import { browserAlerts } from 'utils/alert';

const { PENDING, CONFIRMED, FAILED } = TX_STATUS;

export interface SendTX {
  (
    contract: Contract,
    action: string,
    args: Array<any>,
    options?: any,
    dryRun?: boolean
  ): Promise<void>;
}

interface UseTXReturn {
  sendTx: SendTX;
  txStatus: string | null;
  txMeta: any;
  error: string | null;
}

export function useTx(): UseTXReturn {
  const [error, setError] = useState(null);
  const [txStatus, setTxStatus] = useState<string | null>(null);
  const [txMeta, setTxMeta] = useState({});

  const { active, account, library } = useWeb3React();

  const _sendTx = async (
    contract: Contract,
    action: string,
    args: Array<any>,
    options: any = {},
    dryRun?: boolean
  ) => {
    try {
      if (!active) throw new Error('Web3 wallet is not connected');
      if (!account) throw new Error('Missing web3 account');

      // depending on povider type - we may have start with AWAITING_USER_ACTION
      // or go straight to PENDING
      setTxStatus(PENDING);
      setTxMeta({});
      setError(null);
      console.log('send tx', error, txStatus);
      // NOTE this will fail if the call fails
      const gasLimit = options.gasLimit
        ? options.gasLimit
        : calculateGasMargin(await contract.estimateGas[action](...args));
      // const gasPrice = options.gasPrice || (await getGasPrice());

      if (dryRun) {
        setError(null);
        return setTxStatus(CONFIRMED);
      }

      console.log(options);
      const tx = await contract[action](...args, {
        ...options,
        gasLimit,
        // gasPrice,
      });

      setTxStatus(PENDING);
      setTxMeta({ transactionHash: tx.hash });

      const result = await tx.wait();

      // This tx failed while being mined
      if (result.status === 0) throw new Error('Transaction Failed');

      setTxMeta(result);
      setError(null);
      setTxStatus(result.status === 1 ? CONFIRMED : FAILED);
    } catch (err) {
      console.error(err);

      // check if we can get a revert msg for a failing tx
      let revertMsg;
      try {
        revertMsg = await logRevert({
          contract,
          action,
          args,
          library,
          account,
        });
      } catch (err) {
        //
      }

      setTxMeta({});
      // @ts-ignore
      const errorMsg = revertMsg || (err.data && err.data.message) || err.message;

      console.error(errorMsg);
      browserAlerts.alert(errorMsg, 'error');
      // Important to set error first before FAILED because we might
      // navigate away from the error/status listener before the error is set
      setError(errorMsg);
      setTxStatus(FAILED);
    }
  };

  const sendTx = useCallback(_sendTx, [active, account, setError]);
  return { sendTx, error, txStatus, txMeta };
}

export const estimateGas = async (
  contract: Contract,
  action: string,
  args: Array<any>,
  from: string
) => {
  try {
    const gas = await contract.estimateGas[action](...args);
    return [gas, null];
  } catch (err) {
    // check if we can get a revert msg for a failing tx
    const revertMsg = await logRevert({
      contract,
      action,
      args,
      library: contract.provider,
      account: from,
    });

    // @ts-ignore
    const errorMsg = revertMsg || (err.data && err.data.message) || err.message;
    console.error(errorMsg);
    return [null, errorMsg];
  }
};
